import { useEffect, useRef } from "react";

const glyphs = "01ΔΣΩRPMVANm°C+-/<>[]{}";

export const MatrixRain = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mobile = window.matchMedia("(max-width: 760px)").matches;
    const context = canvas.getContext("2d", { alpha: true });
    let frame = 0;
    let active = false;
    let lastFrame = 0;
    let columns = [];
    let width = 0;
    let height = 0;
    const fontSize = mobile ? 18 : 15;
    const interval = mobile ? 58 : 38;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1 : 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.ceil(width / (fontSize * 1.5));
      columns = Array.from({ length: count }, (_, index) => ({
        x: index * fontSize * 1.5,
        y: -((index * 73) % Math.max(height, 1)),
        speed: mobile ? .45 + (index % 3) * .08 : .52 + (index % 5) * .08,
      }));
    };

    const draw = (timestamp) => {
      if (!active) return;
      frame = window.requestAnimationFrame(draw);
      if (timestamp - lastFrame < interval) return;
      lastFrame = timestamp;
      context.clearRect(0, 0, width, height);
      context.font = `600 ${fontSize}px "Space Grotesk", monospace`;
      context.textAlign = "center";

      columns.forEach((column, index) => {
        const trailLength = mobile ? 5 : 9;
        for (let trail = 0; trail < trailLength; trail += 1) {
          const y = column.y - trail * fontSize * 1.35;
          if (y < -fontSize || y > height + fontSize) continue;
          const opacity = Math.max(.05, .52 - trail * (mobile ? .085 : .052));
          const highlight = trail === 0 && index % 11 === 0;
          const technical = trail === 1 && index % 17 === 0;
          context.fillStyle = highlight
            ? `rgba(239,27,22,${opacity + .25})`
            : technical
              ? `rgba(22,119,255,${opacity + .18})`
              : `rgba(255,255,255,${opacity})`;
          const glyphIndex = Math.abs(Math.floor(column.y / fontSize) + index * 7 - trail * 3) % glyphs.length;
          context.fillText(glyphs[glyphIndex], column.x, y);
        }
        column.y += fontSize * column.speed;
        if (column.y - trailLength * fontSize > height) column.y = -((index * 41) % Math.max(height * .5, 1));
      });
    };

    const renderStatic = () => {
      context.clearRect(0, 0, width, height);
      context.font = `600 ${fontSize}px "Space Grotesk", monospace`;
      columns.forEach((column, index) => {
        context.fillStyle = index % 11 === 0 ? "rgba(239,27,22,.4)" : "rgba(255,255,255,.22)";
        context.fillText(glyphs[(index * 5) % glyphs.length], column.x, (index * 47) % height);
      });
    };

    resize();
    if (reduced) renderStatic();
    const observer = new IntersectionObserver(([entry]) => {
      if (reduced) return;
      active = entry.isIntersecting;
      if (active && !frame) frame = window.requestAnimationFrame(draw);
      if (!active && frame) { window.cancelAnimationFrame(frame); frame = 0; }
    }, { rootMargin: "80px" });
    observer.observe(host);
    window.addEventListener("resize", resize, { passive: true });
    return () => {
      active = false;
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="matrix-rain" data-testid="home-matrix-animation" aria-hidden="true">
      <canvas ref={canvasRef} />
      <div className="matrix-scanline" />
      <div className="matrix-coordinates"><span>MM / FIELD 01</span><span>27.2046° N · 77.4977° E</span></div>
    </div>
  );
};
